import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOfficerTitles } from "@/hooks/useOfficerTitles";
import { useChapters } from "@/hooks/useChapters";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Sparkles, Send, Trash2, Pencil, Users, Calendar,
  FileText, CheckCircle, XCircle, Eye, ClipboardList, Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


function QuizManager() {
  const { active: CHAPTERS } = useChapters();
  const navigate = useNavigate();
  const { all_titles: TITLES } = useOfficerTitles();
  const token = localStorage.getItem('token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [quizzes, setQuizzes] = useState([]);
  const [discordChannels, setDiscordChannels] = useState([]);

  // Create/edit wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1); // 1: meta+material, 2: review questions, 3: audience+publish
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(defaultDraft());
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Attempts viewer
  const [attemptsOpen, setAttemptsOpen] = useState(false);
  const [attemptsData, setAttemptsData] = useState(null);
  const [attemptDetail, setAttemptDetail] = useState(null);

  // BOH context editor
  const [contextOpen, setContextOpen] = useState(false);
  const [contextValue, setContextValue] = useState('');
  const [contextIsDefault, setContextIsDefault] = useState(true);
  const [contextDefault, setContextDefault] = useState('');
  const [contextSaving, setContextSaving] = useState(false);

  // Knowledge sources (the chatbot's "chest") — pickable as quiz material
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [selectedKnowledge, setSelectedKnowledge] = useState({}); // {id: true}

  // Question bank browser
  const [bankOpen, setBankOpen] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [bankItems, setBankItems] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSelected, setBankSelected] = useState({}); // {bankId: true}
  const [bankPickMode, setBankPickMode] = useState(false);
  const [bankEditing, setBankEditing] = useState(null); // bank item being edited

  function defaultDraft() {
    return {
      title: '',
      description: '',
      material: '',
      count: 10,
      passing_pct: 80,
      deadline: '',
      questions: [],
      audience: { chapters: [], titles: [], prospects_only: false, member_ids: [] },
      discord_channel: '',
      // June 2026: per-member randomisation. Defaults for NEW quizzes
      // turn shuffles ON; questions_per_attempt is empty (serve all).
      shuffle_questions: true,
      shuffle_options: true,
      questions_per_attempt: '',
    };
  }

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [list, ch, ks] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/quizzes`, authHeader),
        axios.get(`${BACKEND_URL}/api/quizzes/discord-channels`, authHeader),
        axios.get(`${BACKEND_URL}/api/quizzes/knowledge-sources`, authHeader),
      ]);
      setQuizzes(list.data?.quizzes || []);
      setDiscordChannels(ch.data?.channels || []);
      setKnowledgeSources(ks.data?.sources || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("You don't have permission to manage quizzes.");
        navigate('/');
      } else {
        toast.error('Failed to load quizzes');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /* ----- create / edit wizard ----- */
  const openNewWizard = () => {
    setEditingId(null);
    setDraft(defaultDraft());
    setSelectedKnowledge({});
    setWizardStep(1);
    setWizardOpen(true);
  };

  const openEditWizard = async (id) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/quizzes/${id}`, authHeader);
      const q = res.data;
      setEditingId(id);
      setDraft({
        title: q.title || '',
        description: q.description || '',
        material: q.material || '',
        count: q.questions?.length || 10,
        passing_pct: q.passing_pct || 80,
        deadline: q.deadline || '',
        questions: (q.questions || []).map((qq) => ({ ...qq })),
        audience: {
          chapters: q.audience?.chapters || [],
          titles: q.audience?.titles || [],
          prospects_only: !!q.audience?.prospects_only,
          member_ids: q.audience?.member_ids || [],
        },
        discord_channel: q.discord_channel || '',
        // June 2026: load existing randomization settings if present;
        // existing pre-feature quizzes default to OFF to preserve behaviour.
        shuffle_questions: q.shuffle_questions ?? false,
        shuffle_options: q.shuffle_options ?? false,
        questions_per_attempt: q.questions_per_attempt ?? '',
      });
      setWizardStep(2);
      setWizardOpen(true);
    } catch {
      toast.error('Failed to load quiz');
    }
  };

  const handleGenerate = async () => {
    const selectedIds = Object.keys(selectedKnowledge).filter((k) => selectedKnowledge[k]);
    if (draft.material.trim().length < 40 && selectedIds.length === 0) {
      return toast.error('Paste at least a paragraph of material (40+ chars), or pick at least one knowledge source.');
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/quizzes/generate-questions`,
        {
          material: draft.material,
          count: parseInt(draft.count) || 10,
          knowledge_ids: selectedIds.length ? selectedIds : undefined,
        },
        authHeader
      );
      setDraft({ ...draft, questions: res.data.questions || [] });
      setWizardStep(2);
      toast.success(`Generated ${res.data.count} question${res.data.count === 1 ? '' : 's'}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const toggleKnowledge = (id) => {
    setSelectedKnowledge((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const selectAllKnowledge = () => {
    const all = {};
    knowledgeSources.forEach((k) => { all[k.id] = true; });
    setSelectedKnowledge(all);
  };

  const clearKnowledge = () => setSelectedKnowledge({});

  const updateQuestion = (idx, patch) => {
    const next = [...draft.questions];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, questions: next });
  };

  const updateOption = (qIdx, oIdx, value) => {
    const next = [...draft.questions];
    const opts = [...next[qIdx].options];
    opts[oIdx] = value;
    next[qIdx] = { ...next[qIdx], options: opts };
    setDraft({ ...draft, questions: next });
  };

  const removeQuestion = (idx) => {
    const next = draft.questions.filter((_, i) => i !== idx);
    setDraft({ ...draft, questions: next });
  };

  const addQuestion = () => {
    setDraft({
      ...draft,
      questions: [
        ...draft.questions,
        { text: 'New question', options: ['Option A', 'Option B', 'Option C', 'Option D'], correct_index: 0, rationale: '' },
      ],
    });
  };

  const toggleAudienceItem = (key, value) => {
    const list = draft.audience[key] || [];
    const next = list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
    setDraft({ ...draft, audience: { ...draft.audience, [key]: next } });
  };

  const handleSave = async ({ publish }) => {
    if (!draft.title.trim()) {
      setWizardStep(1);
      return toast.error('Quiz name is required — go back to Step 1 and enter a title');
    }
    if (!draft.questions.length) return toast.error('Generate or add at least one question');
    if (publish && !draft.discord_channel) return toast.error('Pick a Discord channel to publish to');

    const payload = {
      title: draft.title,
      description: draft.description,
      material: draft.material,
      questions: draft.questions,
      passing_pct: parseInt(draft.passing_pct) || 80,
      deadline: draft.deadline || null,
      audience: draft.audience,
      discord_channel: draft.discord_channel || null,
      // June 2026: per-member randomisation
      shuffle_questions: !!draft.shuffle_questions,
      shuffle_options: !!draft.shuffle_options,
      questions_per_attempt: draft.questions_per_attempt
        ? parseInt(draft.questions_per_attempt)
        : null,
    };

    setPublishing(true);
    try {
      let saved;
      if (editingId) {
        const res = await axios.patch(`${BACKEND_URL}/api/quizzes/${editingId}`, payload, authHeader);
        saved = res.data;
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/quizzes`, payload, authHeader);
        saved = res.data;
      }
      // Surface dedup feedback so admins know if duplicates were dropped.
      if (saved?.duplicates_removed > 0) {
        toast.info(
          `${saved.duplicates_removed} duplicate question${saved.duplicates_removed === 1 ? '' : 's'} removed.`,
          { duration: 6000 },
        );
      }
      if (publish) {
        const pub = await axios.post(`${BACKEND_URL}/api/quizzes/${saved.id}/publish`, {}, authHeader);
        if (pub.data?.discord?.ok) {
          toast.success('Quiz posted to Discord channel');
        } else {
          toast.error(`Saved, but Discord post failed: ${pub.data?.discord?.error || 'unknown'}`);
        }
      } else {
        toast.success(editingId ? 'Quiz updated' : 'Quiz saved as draft');
      }
      setWizardOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save quiz');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`Delete quiz "${q.title}" and all its attempts? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/quizzes/${q.id}`, authHeader);
      toast.success('Quiz deleted');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handlePublish = async (q) => {
    if (!q.discord_channel) {
      toast.error('Open the quiz and pick a Discord channel first');
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/quizzes/${q.id}/publish`, {}, authHeader);
      if (res.data?.discord?.ok) {
        toast.success('Posted to Discord channel');
      } else {
        toast.error(`Discord post failed: ${res.data?.discord?.error || 'unknown'}`);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to publish');
    }
  };

  // handleSendReminders removed — quiz DMs disabled per user request (June 2026)

  const openAttempts = async (q) => {
    setAttemptsData(null);
    setAttemptDetail(null);
    setAttemptsOpen(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/quizzes/${q.id}/attempts`, authHeader);
      setAttemptsData(res.data);
    } catch {
      toast.error('Failed to load attempts');
    }
  };

  const reissueOne = async (quizId, memberId) => {
    if (!window.confirm('Reissue this quiz to this member? They will be able to retake (replacing their score).')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/quizzes/${quizId}/reissue`, { member_ids: [memberId] }, authHeader);
      toast.success('Reissued');
      const res = await axios.get(`${BACKEND_URL}/api/quizzes/${quizId}/attempts`, authHeader);
      setAttemptsData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reissue');
    }
  };

  /* ----- BOH context editor ----- */
  const openContextDialog = async () => {
    setContextOpen(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/quizzes/context`, authHeader);
      setContextValue(res.data?.value || '');
      setContextIsDefault(!!res.data?.is_default);
      setContextDefault(res.data?.default || '');
    } catch {
      toast.error('Failed to load BOH context');
    }
  };

  const saveContext = async () => {
    setContextSaving(true);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/quizzes/context`,
        { value: contextValue },
        authHeader
      );
      setContextIsDefault(!!res.data?.is_default);
      toast.success(res.data?.is_default ? 'Reset to default' : 'BOH context updated');
      setContextOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save context');
    } finally {
      setContextSaving(false);
    }
  };

  const resetContextToDefault = () => {
    setContextValue('');
  };

  /* ----- Question Bank browser ----- */
  const openBankBrowser = async (pickMode = true) => {
    setBankPickMode(pickMode && wizardOpen);
    setBankOpen(true);
    setBankSelected({});
    await loadBank('');
  };

  const loadBank = async (search) => {
    setBankLoading(true);
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await axios.get(`${BACKEND_URL}/api/quizzes/question-bank${qs}`, authHeader);
      setBankItems(res.data?.questions || []);
    } catch {
      toast.error('Failed to load question bank');
    } finally {
      setBankLoading(false);
    }
  };

  const handleBankSearch = (val) => {
    setBankSearch(val);
    // Debounce via simple timeout
    clearTimeout(window.__bankSearchTimer);
    window.__bankSearchTimer = setTimeout(() => loadBank(val), 300);
  };

  const toggleBankSelect = (id) => {
    setBankSelected((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const addSelectedFromBank = () => {
    const picked = bankItems.filter((b) => bankSelected[b.id]);
    if (!picked.length) return toast.error('Pick at least one question');
    // Avoid adding duplicates already in the draft (by normalized text)
    const norm = (t) =>
      (t || '').toLowerCase().trim().replace(/\s+/g, ' ').replace(/[^\w\s]/g, '');
    const existingTexts = new Set(draft.questions.map((q) => norm(q.text)));
    const toAdd = [];
    let skipped = 0;
    picked.forEach((b) => {
      if (existingTexts.has(norm(b.text))) {
        skipped++;
        return;
      }
      toAdd.push({
        text: b.text,
        options: b.options,
        correct_index: b.correct_index,
        rationale: b.rationale || '',
      });
    });
    setDraft({ ...draft, questions: [...draft.questions, ...toAdd] });
    setBankOpen(false);
    if (skipped > 0) {
      toast.success(`Added ${toAdd.length}; skipped ${skipped} duplicate(s)`);
    } else {
      toast.success(`Added ${toAdd.length} question(s) from bank`);
    }
  };

  const deleteBankItem = async (item) => {
    if (!window.confirm(`Delete this banked question? It will no longer appear in the bank, but won't affect quizzes already using it.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/quizzes/question-bank/${item.id}`, authHeader);
      toast.success('Removed from bank');
      loadBank(bankSearch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const saveBankEdit = async () => {
    if (!bankEditing) return;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/quizzes/question-bank/${bankEditing.id}`,
        {
          text: bankEditing.text,
          options: bankEditing.options,
          correct_index: bankEditing.correct_index,
          rationale: bankEditing.rationale,
          topics: bankEditing.topics || [],
        },
        authHeader
      );
      toast.success('Banked question updated');
      setBankEditing(null);
      loadBank(bankSearch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save edit');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-3 sm:p-4 md:p-6" data-testid="quizzes-page">
      <div className="max-w-7xl mx-auto space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="border-slate-600 bg-slate-800 hover:bg-slate-700 text-white shrink-0"
              data-testid="quiz-back-btn"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight truncate">Quizzes</h1>
              <p className="text-[11px] sm:text-xs md:text-sm text-slate-400 line-clamp-1">
                AI-generated · audience targeting · Discord posting · auto-grading
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* "Send reminders" hidden — quiz DMs disabled per user request (June 2026) */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBankBrowser(false)}
              className="border-slate-600 bg-slate-800 hover:bg-slate-700 text-white flex-1 sm:flex-none"
              data-testid="quiz-bank-header-btn"
            >
              <FileText className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Question Bank</span>
              <span className="sm:hidden text-xs">Bank</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openContextDialog}
              className="border-slate-600 bg-slate-800 hover:bg-slate-700 text-white flex-1 sm:flex-none"
              data-testid="quiz-context-btn"
            >
              <Sparkles className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Edit BOH Context</span>
              <span className="sm:hidden text-xs">Context</span>
            </Button>
            <Button
              size="sm"
              onClick={openNewWizard}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none"
              data-testid="quiz-new-btn"
            >
              <Plus className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">New Quiz</span>
              <span className="sm:hidden text-xs">New</span>
            </Button>
          </div>
        </div>

        {/* List */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-blue-400" /> All Quizzes
            </CardTitle>
            <CardDescription className="text-slate-400">{quizzes.length} total</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-slate-400">Loading…</div>
            ) : quizzes.length === 0 ? (
              <div className="p-6 text-center text-slate-400">
                No quizzes yet. Click <strong>New Quiz</strong> to create one.
              </div>
            ) : (
              <div className="divide-y divide-slate-700">
                {quizzes.map((q) => (
                  <div key={q.id} className="p-4 hover:bg-slate-700/30 transition-colors" data-testid={`quiz-row-${q.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-white text-base truncate">{q.title}</h3>
                          {q.status === 'published' ? (
                            <Badge className="bg-emerald-600 hover:bg-emerald-600">Published</Badge>
                          ) : q.status === 'closed' ? (
                            <Badge className="bg-slate-600 hover:bg-slate-600">Closed</Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-500 text-amber-400">Draft</Badge>
                          )}
                          <span className="text-xs text-slate-400">
                            {(q.questions || []).length} Q · pass {q.passing_pct}%
                          </span>
                          {q.deadline && (
                            <span className="text-xs text-slate-400">· deadline {q.deadline}</span>
                          )}
                        </div>
                        {q.description && (
                          <p className="text-xs text-slate-400 mt-1 line-clamp-1">{q.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
                          {(q.audience?.chapters || []).map((c) => (
                            <span key={c} className="px-1.5 py-0.5 rounded bg-slate-700/60">{c}</span>
                          ))}
                          {(q.audience?.titles || []).map((t) => (
                            <span key={t} className="px-1.5 py-0.5 rounded bg-slate-700/60">{t}</span>
                          ))}
                          {q.audience?.prospects_only && (
                            <span className="px-1.5 py-0.5 rounded bg-purple-700/60 text-purple-200">Prospects</span>
                          )}
                          {q.discord_channel && (
                            <span className="text-cyan-400">→ #{q.discord_channel}</span>
                          )}
                          <span className="text-slate-500">· {q.attempt_count || 0} attempt(s)</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAttempts(q)}
                          className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                          data-testid={`quiz-attempts-${q.id}`}
                        >
                          <Eye className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">Attempts</span>
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handlePublish(q)}
                          className="bg-blue-600 hover:bg-blue-700"
                          disabled={!q.discord_channel}
                          data-testid={`quiz-publish-${q.id}`}
                        >
                          <Send className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">{q.status === 'published' ? 'Re-post' : 'Publish'}</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditWizard(q.id)}
                          className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                          data-testid={`quiz-edit-${q.id}`}
                          aria-label="Edit"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(q)}
                          className="border-red-700 bg-red-900/40 hover:bg-red-900/60 text-red-300"
                          data-testid={`quiz-delete-${q.id}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Wizard */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Quiz' : 'New Quiz'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Step {wizardStep} of 3 · {wizardStep === 1 ? 'Material & generation' : wizardStep === 2 ? 'Review questions' : 'Audience & publish'}
            </DialogDescription>
          </DialogHeader>

          {wizardStep === 1 && (
            <div className="space-y-3">
              <div>
                <Label>Quiz name</Label>
                <Input
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="e.g. Chapter Bylaws Quiz – Q1 2026"
                  data-testid="quiz-title-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This is the quiz's name — what members will see in the Discord post and on their dashboard.
                </p>
              </div>
              <div>
                <Label>Short description</Label>
                <Input
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="What this quiz covers"
                />
              </div>
              <div>
                <Label>Study material (paste here — anything from a paragraph to a long doc)</Label>
                <Textarea
                  rows={10}
                  value={draft.material}
                  onChange={(e) => setDraft({ ...draft, material: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white font-mono text-sm"
                  placeholder="Paste the source material the questions should test members on… (optional if you pick knowledge sources below)"
                  data-testid="quiz-material-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  GPT-4o will read this and generate 4-option MCQs with subtle distractors. No "all of the above" filler.
                  BOH-TC context (trucker club facts) is automatically prepended — <button type="button" onClick={openContextDialog} className="text-blue-400 hover:underline">edit it here</button>.
                </p>
              </div>

              {/* Knowledge sources picker — pulls from the chatbot's knowledge base */}
              {knowledgeSources.length > 0 && (
                <div className="bg-slate-700/40 rounded-lg p-3 border border-slate-600" data-testid="quiz-knowledge-picker">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm">
                      Or pull from the Hub's Knowledge Base
                      <span className="text-slate-400 text-xs ml-2">
                        ({Object.keys(selectedKnowledge).filter((k) => selectedKnowledge[k]).length} / {knowledgeSources.length} selected)
                      </span>
                    </Label>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={selectAllKnowledge}
                        className="text-xs text-blue-400 hover:underline"
                        data-testid="quiz-kb-select-all"
                      >
                        Select all
                      </button>
                      <span className="text-slate-500">·</span>
                      <button
                        type="button"
                        onClick={clearKnowledge}
                        className="text-xs text-blue-400 hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">
                    The same knowledge the BOH chatbot uses. Pick entries to use as the quiz source material — no need to re-paste.
                  </p>
                  <div className="max-h-44 overflow-y-auto space-y-1">
                    {knowledgeSources.map((k) => (
                      <label
                        key={k.id}
                        className={`flex items-start gap-2 p-2 rounded border cursor-pointer ${
                          selectedKnowledge[k.id]
                            ? 'border-blue-500 bg-blue-900/30'
                            : 'border-slate-600 bg-slate-800/40 hover:bg-slate-700/40'
                        }`}
                        data-testid={`quiz-kb-${k.id}`}
                      >
                        <Checkbox
                          checked={!!selectedKnowledge[k.id]}
                          onCheckedChange={() => toggleKnowledge(k.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-white font-medium">{k.title}</span>
                            {k.admin_only && (
                              <Badge variant="outline" className="border-amber-500 text-amber-400 text-[10px]">
                                Admin only
                              </Badge>
                            )}
                            <span className="text-[10px] text-slate-500">{k.length.toLocaleString()} chars</span>
                          </div>
                          {k.preview && (
                            <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{k.preview}…</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>How many questions?</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={draft.count}
                    onChange={(e) => setDraft({ ...draft, count: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="quiz-count-input"
                  />
                </div>
                <div>
                  <Label>Passing %</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={draft.passing_pct}
                    onChange={(e) => setDraft({ ...draft, passing_pct: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="quiz-passing-input"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setWizardOpen(false)}
                  className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="quiz-generate-btn"
                >
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate questions with AI</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-300">
                  Review and edit the AI-generated questions. Click the correct option to mark it.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openBankBrowser(true)}
                    className="border-blue-700 bg-blue-900/40 hover:bg-blue-900/60 text-blue-300"
                    data-testid="quiz-browse-bank-btn"
                  >
                    <FileText className="w-3 h-3 mr-1" /> Browse bank
                  </Button>
                  <Button size="sm" variant="outline" onClick={addQuestion} className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white">
                    <Plus className="w-3 h-3 mr-1" /> Add manual
                  </Button>
                </div>
              </div>
              {draft.questions.length === 0 && (
                <div className="text-center text-slate-400 p-6 bg-slate-700/30 rounded">
                  No questions yet. Go back to step 1 and generate, or click "Add manual question".
                </div>
              )}
              {draft.questions.map((q, idx) => (
                <div key={idx} className="bg-slate-700/40 rounded p-3 border border-slate-600 space-y-2" data-testid={`quiz-q-${idx}`}>
                  <div className="flex justify-between items-start gap-2">
                    <Label className="text-xs text-slate-400">Question {idx + 1}</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeQuestion(idx)}
                      className="h-7 border-red-700 bg-red-900/40 hover:bg-red-900/60 text-red-300"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <Textarea
                    rows={2}
                    value={q.text}
                    onChange={(e) => updateQuestion(idx, { text: e.target.value })}
                    className="bg-slate-800 border-slate-600 text-white text-sm"
                  />
                  <div className="space-y-1">
                    {(q.options || []).map((opt, oi) => (
                      <div
                        key={oi}
                        className={`flex items-center gap-2 p-2 rounded border ${
                          q.correct_index === oi
                            ? 'border-emerald-500 bg-emerald-900/30'
                            : 'border-slate-600 bg-slate-800/40'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => updateQuestion(idx, { correct_index: oi })}
                          className={`text-xs font-bold w-7 h-7 sm:w-6 sm:h-6 rounded-full shrink-0 ${
                            q.correct_index === oi ? 'bg-emerald-600 text-white' : 'bg-slate-600 text-slate-300'
                          }`}
                          title="Mark correct"
                          data-testid={`quiz-q-${idx}-opt-${oi}-correct`}
                        >
                          {q.correct_index === oi ? '✓' : String.fromCharCode(65 + oi)}
                        </button>
                        <Input
                          value={opt}
                          onChange={(e) => updateOption(idx, oi, e.target.value)}
                          className="bg-transparent border-0 text-white text-sm focus-visible:ring-0"
                        />
                      </div>
                    ))}
                  </div>
                  <Input
                    value={q.rationale || ''}
                    onChange={(e) => updateQuestion(idx, { rationale: e.target.value })}
                    className="bg-slate-800 border-slate-600 text-slate-300 text-xs"
                    placeholder="(Optional) why this is the correct answer"
                  />
                </div>
              ))}
              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={() => setWizardStep(1)} className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white">
                  Back
                </Button>
                <Button
                  onClick={() => setWizardStep(3)}
                  disabled={!draft.questions.length}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="quiz-next-step3"
                >
                  Next — audience & publish →
                </Button>
              </DialogFooter>
            </div>
          )}

          {wizardStep === 3 && (
            <div className="space-y-4">
              <div>
                <Label>Audience — chapters (leave empty to allow all)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {CHAPTERS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleAudienceItem('chapters', c)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                        draft.audience.chapters.includes(c)
                          ? 'bg-cyan-600 border-cyan-400 text-white'
                          : 'bg-slate-700 border-slate-600 text-slate-300'
                      }`}
                      data-testid={`quiz-aud-chapter-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Audience — titles (leave empty to allow all)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {TITLES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleAudienceItem('titles', t)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                        draft.audience.titles.includes(t)
                          ? 'bg-purple-600 border-purple-400 text-white'
                          : 'bg-slate-700 border-slate-600 text-slate-300'
                      }`}
                      data-testid={`quiz-aud-title-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={draft.audience.prospects_only}
                  onCheckedChange={(v) =>
                    setDraft({ ...draft, audience: { ...draft.audience, prospects_only: !!v } })
                  }
                  data-testid="quiz-aud-prospects"
                />
                <Label className="text-sm">Restrict to prospects only ((pm) / PM titles)</Label>
              </div>
              <div>
                <Label>Deadline (optional)</Label>
                <Input
                  type="date"
                  value={draft.deadline ? String(draft.deadline).substring(0, 10) : ''}
                  onChange={(e) => setDraft({ ...draft, deadline: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="quiz-deadline-input"
                />
                <p className="text-xs text-slate-500 mt-1">After this date, the link is disabled.</p>
              </div>

              {/* Per-member randomization controls (June 2026) */}
              <div className="rounded-lg border border-slate-700 bg-slate-900/30 p-3 space-y-3">
                <Label className="text-sm font-semibold text-white">
                  Randomize per member
                </Label>
                <p className="text-xs text-slate-400 -mt-1">
                  Each member gets a deterministically-different view of the quiz, so two members can&apos;t share answers.
                </p>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!draft.shuffle_questions}
                    onCheckedChange={(v) => setDraft({ ...draft, shuffle_questions: !!v })}
                    data-testid="quiz-shuffle-questions"
                  />
                  <Label className="text-sm">Shuffle question order per member</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!draft.shuffle_options}
                    onCheckedChange={(v) => setDraft({ ...draft, shuffle_options: !!v })}
                    data-testid="quiz-shuffle-options"
                  />
                  <Label className="text-sm">Shuffle answer options per question</Label>
                </div>
                <div>
                  <Label className="text-sm">Questions per attempt (optional)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={draft.questions.length || 1}
                    placeholder={`Leave blank to serve all ${draft.questions.length || 0}`}
                    value={draft.questions_per_attempt}
                    onChange={(e) =>
                      setDraft({ ...draft, questions_per_attempt: e.target.value })
                    }
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="quiz-per-attempt-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {draft.questions_per_attempt && draft.questions.length
                      ? `Each member sees ${draft.questions_per_attempt} of ${draft.questions.length} random questions.`
                      : 'Blank = each member sees the entire question pool.'}
                  </p>
                </div>
              </div>
              <div>
                <Label>Discord channel to post the quiz link</Label>
                <Select
                  value={draft.discord_channel}
                  onValueChange={(v) => setDraft({ ...draft, discord_channel: v })}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="quiz-channel-select">
                    <SelectValue placeholder="Pick a channel…" />
                  </SelectTrigger>
                  <SelectContent>
                    {discordChannels.map((c) => (
                      <SelectItem key={c.key} value={c.key} disabled={!c.configured}>
                        #{c.key} {!c.configured && '(no webhook configured)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={() => setWizardStep(2)} className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white">
                  Back
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSave({ publish: false })}
                  disabled={publishing}
                  className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white"
                  data-testid="quiz-save-draft-btn"
                >
                  Save as draft
                </Button>
                <Button
                  onClick={() => handleSave({ publish: true })}
                  disabled={publishing}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="quiz-publish-now-btn"
                >
                  {publishing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Save & publish to Discord</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attempts viewer */}
      <Dialog open={attemptsOpen} onOpenChange={setAttemptsOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-4xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{attemptsData?.quiz?.title || 'Quiz Attempts'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {attemptsData ? `${attemptsData.attempts?.length || 0} attempts` : 'Loading…'}
            </DialogDescription>
          </DialogHeader>
          {!attemptsData ? (
            <div className="p-6 text-center text-slate-400">Loading…</div>
          ) : (attemptsData.attempts || []).length === 0 ? (
            <div className="p-6 text-center text-slate-400">No attempts yet.</div>
          ) : (
            <div className="space-y-2">
              {attemptsData.attempts.map((a) => (
                <div
                  key={a.id}
                  className="bg-slate-700/40 rounded p-3 border border-slate-600"
                  data-testid={`attempt-row-${a.id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{a.member_handle || a.user_username}</span>
                        {a.passed ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-600">
                            <CheckCircle className="w-3 h-3 mr-1" /> Pass
                          </Badge>
                        ) : (
                          <Badge className="bg-red-600 hover:bg-red-600">
                            <XCircle className="w-3 h-3 mr-1" /> Fail
                          </Badge>
                        )}
                        {a.retake_allowed && (
                          <Badge variant="outline" className="border-amber-500 text-amber-400">Retake unlocked</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {a.score}/{a.total} ({a.percentage}%) · submitted {a.submitted_at && new Date(a.submitted_at).toLocaleString()}
                        {a.history?.length ? ` · ${a.history.length} prior attempt(s)` : ''}
                      </div>
                      {a.notifications && a.notifications.attempted > 0 && (
                        <div className="text-[10px] text-slate-500 mt-0.5" title={(a.notifications.recipients || []).map((r) => `${r.role}: ${r.handle} ${r.ok ? '✓' : '✗'}`).join(' · ')}>
                          📨 Notified {a.notifications.succeeded}/{a.notifications.attempted} officers
                          {a.notifications.failed ? ` (${a.notifications.failed} failed)` : ''}
                          {a.notifications.skipped_no_discord ? ` · ${a.notifications.skipped_no_discord} no Discord` : ''}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAttemptDetail(a)}
                        className="border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                        data-testid={`attempt-view-${a.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" /> Review
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => reissueOne(a.quiz_id, a.member_id)}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid={`attempt-reissue-${a.id}`}
                      >
                        Reissue
                      </Button>
                    </div>
                  </div>
                  {attemptDetail?.id === a.id && (
                    <div className="mt-3 pt-3 border-t border-slate-600 space-y-2">
                      {(a.review || []).map((r, i) => (
                        <div
                          key={i}
                          className={`text-xs rounded p-2 ${
                            r.is_right
                              ? 'bg-emerald-900/30 border border-emerald-700'
                              : 'bg-red-900/30 border border-red-700'
                          }`}
                        >
                          <div className="text-slate-300">
                            Q{i + 1}: chose {r.chosen_index != null ? String.fromCharCode(65 + r.chosen_index) : '—'}, correct{' '}
                            {r.correct_index != null ? String.fromCharCode(65 + r.correct_index) : '—'}{' '}
                            {r.is_right ? '✓' : '✗'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* BOH Context editor */}
      <Dialog open={contextOpen} onOpenChange={setContextOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-400" /> BOH Context Preamble
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This text is silently prepended to every quiz's study material before the AI generates questions.
              Use it to anchor the AI in BOH-TC facts (trucker club, chapters, officer titles, prospect rules)
              so members never get off-brand or MC-flavored questions, even when pasted material is sparse.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs">
              {contextIsDefault ? (
                <Badge variant="outline" className="border-slate-500 text-slate-400">
                  Using built-in default
                </Badge>
              ) : (
                <Badge className="bg-emerald-600 hover:bg-emerald-600">Customized</Badge>
              )}
              <button
                type="button"
                onClick={resetContextToDefault}
                className="text-xs text-blue-400 hover:underline ml-auto"
                data-testid="quiz-context-reset-btn"
              >
                Reset to built-in default
              </button>
            </div>
            <Textarea
              rows={14}
              value={contextValue}
              onChange={(e) => setContextValue(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white text-sm font-mono"
              placeholder="Empty = use built-in default"
              data-testid="quiz-context-textarea"
            />
            {!contextValue.trim() && (
              <div className="bg-slate-900/60 border border-slate-700 rounded p-3 max-h-48 overflow-y-auto">
                <div className="text-xs text-slate-400 mb-1">Built-in default (used when empty):</div>
                <pre className="text-xs text-slate-300 whitespace-pre-wrap">{contextDefault}</pre>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setContextOpen(false)}
              className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={saveContext}
              disabled={contextSaving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="quiz-context-save-btn"
            >
              {contextSaving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Question Bank browser */}
      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" /> Question Bank
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {bankPickMode
                ? 'Pick questions to add to the quiz you\'re building. Duplicates already in the draft are skipped automatically.'
                : 'Browse, edit, or remove questions saved from past AI generations and quizzes.'}
            </DialogDescription>
          </DialogHeader>

          {/* Editor mode */}
          {bankEditing ? (
            <div className="space-y-3" data-testid="bank-edit-form">
              <div>
                <Label>Question</Label>
                <Textarea
                  rows={2}
                  value={bankEditing.text}
                  onChange={(e) => setBankEditing({ ...bankEditing, text: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white text-sm"
                />
              </div>
              {(bankEditing.options || []).map((opt, oi) => (
                <div
                  key={oi}
                  className={`flex items-center gap-2 p-2 rounded border ${
                    bankEditing.correct_index === oi
                      ? 'border-emerald-500 bg-emerald-900/30'
                      : 'border-slate-600 bg-slate-700/40'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setBankEditing({ ...bankEditing, correct_index: oi })}
                    className={`text-xs font-bold w-6 h-6 rounded-full ${
                      bankEditing.correct_index === oi ? 'bg-emerald-600 text-white' : 'bg-slate-600 text-slate-300'
                    }`}
                  >
                    {bankEditing.correct_index === oi ? '✓' : String.fromCharCode(65 + oi)}
                  </button>
                  <Input
                    value={opt}
                    onChange={(e) => {
                      const opts = [...bankEditing.options];
                      opts[oi] = e.target.value;
                      setBankEditing({ ...bankEditing, options: opts });
                    }}
                    className="bg-transparent border-0 text-white text-sm"
                  />
                </div>
              ))}
              <div>
                <Label>Rationale</Label>
                <Input
                  value={bankEditing.rationale || ''}
                  onChange={(e) => setBankEditing({ ...bankEditing, rationale: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white text-sm"
                />
              </div>
              <div>
                <Label>Topics (comma-separated)</Label>
                <Input
                  value={(bankEditing.topics || []).join(', ')}
                  onChange={(e) =>
                    setBankEditing({
                      ...bankEditing,
                      topics: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                    })
                  }
                  className="bg-slate-700 border-slate-600 text-white text-sm"
                  placeholder="e.g. bylaws, officer roles, prospects"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setBankEditing(null)}
                  className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white"
                >
                  Cancel
                </Button>
                <Button onClick={saveBankEdit} className="bg-blue-600 hover:bg-blue-700">
                  Save changes
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              {/* Browse mode */}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search bank by question text…"
                  value={bankSearch}
                  onChange={(e) => handleBankSearch(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="quiz-bank-search"
                />
                {bankPickMode && (
                  <Badge variant="outline" className="border-blue-500 text-blue-400">
                    {Object.keys(bankSelected).length} picked
                  </Badge>
                )}
              </div>
              <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                {bankLoading ? (
                  <div className="text-center text-slate-400 p-6">Loading…</div>
                ) : bankItems.length === 0 ? (
                  <div className="text-center text-slate-400 p-6 bg-slate-700/30 rounded">
                    {bankSearch ? 'No matches.' : 'Bank is empty. Generate questions to fill it.'}
                  </div>
                ) : (
                  bankItems.map((b) => {
                    const picked = !!bankSelected[b.id];
                    return (
                      <div
                        key={b.id}
                        className={`bg-slate-700/40 rounded p-3 border ${
                          picked ? 'border-blue-500' : 'border-slate-600'
                        }`}
                        data-testid={`bank-item-${b.id}`}
                      >
                        <div className="flex items-start gap-2">
                          {bankPickMode && (
                            <Checkbox
                              checked={picked}
                              onCheckedChange={() => toggleBankSelect(b.id)}
                              className="mt-1"
                              data-testid={`bank-pick-${b.id}`}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-white text-sm">{b.text}</div>
                            <div className="space-y-0.5 mt-1.5">
                              {(b.options || []).map((opt, oi) => (
                                <div
                                  key={oi}
                                  className={`text-xs ${
                                    oi === b.correct_index ? 'text-emerald-400 font-medium' : 'text-slate-400'
                                  }`}
                                >
                                  {String.fromCharCode(65 + oi)}. {opt}
                                  {oi === b.correct_index && ' ✓'}
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500">
                              <span>used {b.used_count || 0}x</span>
                              {(b.topics || []).map((t) => (
                                <span key={t} className="px-1.5 py-0.5 rounded bg-purple-700/40 text-purple-200">
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setBankEditing({ ...b })}
                              className="h-7 border-slate-500 bg-slate-700 hover:bg-slate-600 text-white"
                              data-testid={`bank-edit-${b.id}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteBankItem(b)}
                              className="h-7 border-red-700 bg-red-900/40 hover:bg-red-900/60 text-red-300"
                              data-testid={`bank-delete-${b.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setBankOpen(false)}
                  className="border-slate-600 bg-slate-700 hover:bg-slate-600 text-white"
                >
                  Close
                </Button>
                {bankPickMode && (
                  <Button
                    onClick={addSelectedFromBank}
                    disabled={Object.keys(bankSelected).length === 0}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="bank-add-selected-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add {Object.keys(bankSelected).length || ''} to quiz
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default QuizManager;
